<template lang="pug">
  form(v-if="utilsLoaded" @submit.prevent="handleSubmit")
    slot(name="header")

    div.vue__form-oauth-lead-alert(v-if="!authenticated && userLeadId")
      div.vue__form-oauth-lead-alert-icon-container
        OauthIcon(:type="oauthProvider" :active="true")
      | Ваш аккаунт социальной сети еще не привязан к Premium Management. #{' '}
      | Для завершения регистрации заполните недостающие поля.

    TextInput(
      name="name"
      placeholder="Ваши имя, фамилия"
      v-model.trim="user.name"
      v-validate="'required|min:3|max:80'"
      data-vv-as='"Имя"'
      :forceValidate="validated"
      :error="errors.first('name')"
      :readonly="areInputsReadonly"
    ): FaIcon(slot="icon" icon="user")

    TextInput(
      type="email"
      name="email"
      placeholder="Ваш email адрес"
      v-model.trim="user.email"
      v-validate="'required|email|max:80'"
      data-vv-as='"Email"'
      :forceValidate="validated"
      :error="errors.first('email')"
      :readonly="areInputsReadonly"
    ): FaIcon(slot="icon" icon="at")

    TelInput(
      name="phone"
      v-model="user.phone"
      placeholder="Ваш номер телефона"
      v-validate="{ required: true, min: 6, max: 20 }"
      data-vv-as='"Телефон"'
      :forceValidate="validated"
      :error="errors.first('phone')"
      :readonly="areInputsReadonly"
    )

    slot

    .vue__form-checkboxes
      slot(name="checkboxes")

      CheckboxInput(
        v-if="!authenticated"
        v-model="user.newsletter"
        name="newsletter"
        :disabled="areInputsReadonly"
      ) Хочу максимально эффективно использовать ресурс: получать рассылки с эксклюзивными предложениями и советами по обучению


      template(v-if="!authenticated")
        CheckboxInput(
          v-if="!authenticated"
          v-model="user.gdprConfirmed"
          name="gdpr_confirmed"
          v-validate="'required:true'"
          :validated="validated"
          :error="errors.first('gdpr_confirmed')"
          :disabled="areInputsReadonly"
        )
          | Я принимаю&#32;
          a(href='/terms-of-service' target='_blank') условия пользовательского соглашения
          | ,&#32;
          a(href='https://drive.google.com/file/d/1P8kMcyesPWLsrnziaKWHyE_36yZVxZBh/view?usp=sharing' target='_blank') договора оферты
          | &#32;и даю согласие на обработку моих персональных данных

    .vue__form-pseudo-logout(v-if="authenticated")
      a(href="#" @click.prevent="handleOtherUserLinkClick") Другой пользователь?

    .vue__form-oauth-wrapper(v-if="!authenticated && showOauthIcons")
      hr
      OauthLinks(:selected="oauthProvider")

    .vue__form-footer
      BaseButton(v-if="!submitting" type="submit" @click.native="trackSubmitClick") Отправить
      div(v-else)
        SimpleSpinner
        small Отправляем


    VueRecaptcha(:sitekey="siteKey" size="invisible" type="invisible" ref="recaptcha" @verify="submitForm")
    #recaptcha_footnote(v-if="!authenticated"): small
      | Используется защита от спама reCaptcha от Google
      br
      a(href="https://www.google.com/intl/ru/policies/privacy/" target="_blank") Конфиденциальность
      | #{' '}|#{' '}
      a(href="https://www.google.com/intl/ru/policies/terms/" target="_blank") Условия использования
  SimpleSpinner(v-else)
</template>

<script>
  import {
    mapMutations,
    mapState,
  } from 'vuex';

  import VueRecaptcha from 'vue-recaptcha';
  import { siteKey } from '@app_shared/recaptcha.js.erb';

  export default {
    components: {
      VueRecaptcha,
    },

    data () {
      return {
        user: {
          email: '',
          name: '',
          phone: '',
          gdprConfirmed: false,
          newsletter: false,
          recaptchaResponse: null,
        },

        validated: false,
        utilsLoaded: false,
      };
    },

    computed: {
      ...mapState([
        'authenticated',
        'submitting',
        'submitted',
        'userName',
        'userEmail',
        'userPhone',
        'userLeadId',
        'oauthProvider',
        'showOauthIcons',
      ]),

      areInputsReadonly () {
        return this.authenticated || this.submitting || this.submitted;
      },
    },

    watch: {
      userName: {
        handler (now) { this.user.name = now; },
        immediate: true,
      },
      userEmail: {
        handler (now) { this.user.email = now; },
        immediate: true,
      },
      userPhone: {
        handler (now) { this.user.phone = now; },
        immediate: true,
      },
    },

    created () {
      this.siteKey = siteKey;

      this.handleQueryAuthParams();

      const unwatch = this.$watch('user', () => {
        if (window.VK) {
          VK.Retargeting.Event('form-interacted');
        } else {
          console.log('change');
        }

        unwatch();
      }, {
        deep: true,
      });
    },

    async mounted () {
      await window.intlTelInputGlobals.loadUtils('https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/12.1.12/js/utils.js');
      this.utilsLoaded = true;
      this.$validator.validate();
    },

    methods: {
      ...mapMutations(['pseudoLogout']),

      handleSubmit () {
        this.$validator.validate().then((valid) => {
          if (valid) {
            if (!this.authenticated) {
              this.$refs.recaptcha.execute();
            } else {
              this.submitForm();
            }
          }
        });

        this.validated = true;
      },

      handleOtherUserLinkClick () {
        this.pseudoLogout();
      },

      submitForm (response) {
        this.$refs.recaptcha.reset();
        this.user.recaptchaResponse = response;
        this.$emit('submit', this.user);
      },

      trackSubmitClick () {
        if (window.VK) {
          VK.Retargeting.Event('submit-btn-clicked');
        } else {
          console.log('click');
        }
      },

      handleQueryAuthParams () {
        if (this.authenticated) return;

        const urlParams = new URLSearchParams(window.location.search);
        const authKeys = ['name', 'email', 'phone'];
        const authValues = authKeys.map(key => urlParams.get(key));

        if (!authValues.some(v => v)) return;

        authKeys.forEach((key, index) => {
          this.user[key] = authValues[index];
          urlParams.delete(key);
        });

        let resultPath = window.location.pathname;
        const newUrlParamsString = urlParams.toString();

        if (newUrlParamsString.length) resultPath += '?' + newUrlParamsString;

        window.history.replaceState(null, null, resultPath);
      },
    },
  };
</script>

<style lang="scss" scoped>

  .vue__form-footer {
    margin-top: 20px;
    text-align: center;
  }

  .vue__form-checkboxes {
    margin-top: 16px;
  }

  .vue__form-pseudo-logout {
    text-align: center;
    margin-top: 12px;

    a {
      color: #999;
    }
  }

  .vue__form-oauth-wrapper {
    hr {
      margin-top: 20px;
      margin-bottom: 20px;
      background: transparent;
      border-bottom-color: #f3f3f3;
      border-left: none;
      border-top: none;
    }
  }

  .vue__form-oauth-lead-alert {
    padding: 16px 12px 12px;
    text-align: center;
    margin-bottom: 6px;
    border-radius: 4px;
    font-size: .85rem;
    border: 2px solid $java;
    position: relative;
    background: #f6feff;
    color: #006d79;

    .vue__form-oauth-lead-alert-icon-container {
      position: absolute;
      left: 0;
      right: 0;
      top: -16px;
      text-align: center;

      img {
        display: inline-block;
      }
    }
  }

  #recaptcha_footnote {
    color: $oslo-gray;
    text-align: center;
    margin-top: 16px;
  }
</style>
